import { View, Text, StyleSheet } from '@react-pdf/renderer';

import { PDF_COLORS, PDF_FONT_FAMILY } from './styles';

export interface MetadataField {
  label: string;
  value?: string | string[];
}

interface MetadataBoxProps {
  primary: MetadataField[];
  secondary?: MetadataField[];
}

const styles = StyleSheet.create({
  wrapper: {
    marginTop: 12,
    marginHorizontal: 30,
    gap: 2,
  },
  block: {
    backgroundColor: PDF_COLORS.metadataBoxBg,
    borderRadius: 1,
    paddingHorizontal: 12,
    paddingVertical: 9,
    flexDirection: 'row',
  },
  // Each row slices the block into N equal columns so labels never get pushed
  // off-screen by long chip lists; chips wrap *inside* their own column.
  cell: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 4,
    paddingRight: 8,
  },
  label: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 9,
    color: PDF_COLORS.metadataLabel,
    paddingTop: 2,
    marginRight: 2,
  },
  plainValue: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 400,
    fontSize: 9,
    color: PDF_COLORS.metadataLabel,
    paddingTop: 2,
  },
  chip: {
    color: PDF_COLORS.metadataChipText,
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 500,
    fontSize: 9,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 1,
  },
  chipPrimary: {
    backgroundColor: PDF_COLORS.metadataChipBgPrimary,
  },
  chipSecondary: {
    backgroundColor: PDF_COLORS.metadataChipBgSecondary,
  },
});

const normalizeValue = (value: MetadataField['value']): string[] => {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value.filter((v) => v && String(v).trim().length > 0);
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? [trimmed] : [];
};

const Cell = ({
  field,
  chipify,
  widthPercent,
  variant,
}: {
  field: MetadataField;
  chipify: boolean;
  widthPercent: number;
  variant: 'primary' | 'secondary';
}) => {
  const values = normalizeValue(field.value);
  const chipStyle = [
    styles.chip,
    variant === 'primary' ? styles.chipPrimary : styles.chipSecondary,
  ];

  return (
    <View style={[styles.cell, { width: `${widthPercent}%` }]}>
      <Text style={styles.label}>{`${field.label}:`}</Text>
      {values.length === 0 ? null : chipify ? (
        values.map((value) => (
          <Text key={`${field.label}-${value}`} style={chipStyle}>
            {value}
          </Text>
        ))
      ) : (
        <Text style={styles.plainValue}>{values.join(', ')}</Text>
      )}
    </View>
  );
};

const Block = ({
  fields,
  variant,
}: {
  fields: MetadataField[];
  variant: 'primary' | 'secondary';
}) => {
  // Always render 3 columns so spacing stays consistent across blocks.
  const cells: Array<MetadataField | null> = [...fields];
  while (cells.length < 3) cells.push(null);
  const visible = cells.slice(0, 3);
  const widthPercent = 100 / visible.length;

  return (
    <View style={styles.block} wrap={false}>
      {visible.map((field, idx) => {
        if (!field) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <View key={`spacer-${idx}`} style={{ width: `${widthPercent}%` }} />
          );
        }
        return (
          <Cell
            key={field.label}
            field={field}
            chipify={field.label.toLowerCase() !== 'date'}
            widthPercent={widthPercent}
            variant={variant}
          />
        );
      })}
    </View>
  );
};

const MetadataBox = ({ primary, secondary }: MetadataBoxProps) => {
  const hasPrimary = primary.some((f) => normalizeValue(f.value).length > 0);
  const hasSecondary = (secondary ?? []).some((f) => normalizeValue(f.value).length > 0);

  if (!hasPrimary && !hasSecondary) return null;

  return (
    <View style={styles.wrapper}>
      {hasPrimary && <Block fields={primary} variant="primary" />}
      {hasSecondary && <Block fields={secondary ?? []} variant="secondary" />}
    </View>
  );
};

export default MetadataBox;
